// @ts-check

// ========== API Response Types ==========

/**
 * @typedef {Object} SharedWithUser
 * @property {number} id - The id of the user.
 * @property {string} name - The name of the user.
 * @property {boolean} isActive - Whether the account is currently active.
 */

/**
 * @typedef {Object} SearchExportTemplateListItem
 * @property {number} id
 * @property {string} name
 * @property {number} numberOfFields - Total fields persisted on the template (license-agnostic).
 * @property {number} availableFieldCount - Fields visible to the current user given their licence. Industry-licensed (VC funding) columns are excluded for academic-only users. Used in the template list display.
 * @property {string} ownerName
 * @property {number} ownerId
 * @property {number?} sharedFromTemplateId
 * @property {SharedWithUser[]} sharedWith
 * @property {string} lastUpdated
 * @property {string} dateCreated
 */

/**
 * @typedef {Object} SearchExportTemplatesResponse
 * @property {number?} favouriteId
 * @property {SearchExportTemplateListItem[]} data
 * @property {number} totalCount
 */

/**
 * @typedef {number} ColumnRequirement
 */

/**
 * @typedef {Object} ColumnMetadata
 * @property {string} id - The id of the column metadata.
 * @property {string} name - The name of the column metadata.
 * @property {number} width - The width of the column metadata.
 * @property {string} tooltip - The tooltip of the column metadata.
 * @property {boolean} mandatory - Whether the column metadata is mandatory.
 * @property {ColumnRequirement[]} requirements - The requirements of the column metadata.
 */

/**
 * @typedef {Object} SheetMetadata
 * @property {string} id - The id of the sheet metadata.
 * @property {string} name - The name of the sheet metadata.
 * @property {ColumnMetadata[]} columns - The columns of the sheet metadata.
 */

/**
 * The response from the API for getting an export template column.
 * @typedef {Object} ExportTemplateColumn
 * @property {string} columnMetadataId - The id of the column metadata.
 * @property {string | null} overridenName - The overriden name of the column.
 */

/**
 * The response from the API for getting an export template sheet.
 * @typedef {Object} ExportTemplateSheet
 * @property {string} sheetMetadataId - The id of the sheet metadata.
 * @property {string | null} overridenName - The overriden name of the sheet.
 * @property {ExportTemplateColumn[]} columns - The columns of the sheet.
 */

/**
 * The response from the API for getting an export template.
 * @typedef {Object} ExportTemplate
 * @property {number | null} id - The id of the export template.
 * @property {string} name - The name of the export template.
 * @property {ExportTemplateSheet[]} sheets - The sheets of the export template.
 */

/**
 * @typedef {Object} GetExportTemplateResponse
 * @property {number | null} id - The id of the export template.
 * @property {string} name - The name of the export template.
 * @property {ExportTemplateSheet[]} sheets - The sheets of the export template.
 * @property {number?} sharedFromTemplateId
 * @property {string?} ownerUserName
 * @property {number?} ownerUserId
 * @property {SharedWithUser[]} sharedWith - The user ids of the users the template is shared with.
 */

// ========== Internal State Types ==========

/**
 * @typedef {'closed' | 'edit' | 'editColumns' | 'save' | 'settings'} ModalView
 */

/**
 * Identifies the entry point the editor was opened from. The editor renders
 * the same tabs and fields in both cases; this value only governs whether
 * the "Export now without saving" action is offered (via the
 * `oneTimeExportAvailable` selector).
 *
 * @typedef {'template_management' | 'search'} ModalViewContext
 */

/**
 * @typedef {'new_template' | 'update_existing_template'} SaveContext
 */

/**
 * @typedef {Object} SearchConfig
 * @property {string | null} searchFilter - The search filter to apply to the templates.
 * @property {string | null} searchDateOption - The date option to apply to the templates.
 * @property {string | null} searchDateRange - The date range to apply to the templates.
 * @property {number} pageSize - The number of templates to fetch per page.
 * @property {number} pageNumber - The page number to fetch.
 * @property {string} orderBy - The field to order the templates by.
 * @property {'asc' | 'desc'} order - The order to sort the templates by.
 * @property {number} mutationCount - The number of mutations to the search config. Used to trigger a refresh of the template list.
 */

/**
 * @typedef {Object} DropdownTemplate
 * @property {number} id - The id of the dropdown template.
 * @property {string} name - The name of the dropdown template.
 * @property {number} fieldCount - The total number of fields persisted on the template.
 * @property {number} availableFieldCount - The number of fields available to the
 *   current user, with VC-funding (industry-licence) columns excluded for academic-only
 *   users. Used in dropdown labels.
 */

/**
 * Sheets and columns being edited. Has extra UI data to capture edit state view.
 * The idea is to preserve data the server might not need but the UI does.
 *
 * @typedef {Object} EditingColumn
 * @property {string} columnMetadataId - The id of the column metadata.
 * @property {string | null} overridenName - The overriden name of the column.
 * @property {string} defaultName - The name of the column as defined in the server metadata.
 * @property {boolean} enabled - Whether or not the column is enabled
 * @property {boolean} mandatory - Cache whether this column is mandatory
 * @property {ColumnRequirement[]} requirements - License requirements for this column
 * @property {boolean} [orphaned] - True when the column is stored on the template but missing from current metadata; hidden from editor, round-tripped on save.
 * @property {boolean} [available] - When set to false, the column is currently unusable by the user under their licence. Computed at load time whenever `hasLicense` is supplied. Orthogonal to `enabled` (persisted user intent); the editor and select-all/count helpers skip unavailable columns, but `getEditingSheetsPayload` keeps them in the save payload so a licence re-upgrade restores the user's prior selections.
 *
 * @typedef {Object} EditingSheet
 * @property {string} sheetMetadataId - The id of the sheet metadata.
 * @property {string | null} overridenName - The overriden name of the sheet.
 * @property {string} defaultName - The name of the sheet as defined in the server metadata.
 * @property {Record<string, EditingColumn>} columnMap - The columns of the sheet.
 * @property {string[]} columnOrder - The order of column ids for display.
 * @property {boolean} enabled - Whether or not the column is enabled
 * @property {boolean} mandatory - Cache whether this sheet is mandatory
 * @property {number} selectedColumns - Count of how many columns are selected
 * @property {number} totalColumns - Count of how many columns total
 * @property {string[]} orphanedColumnIds - Ids of orphan columns kept only for save round-trip; appended after visible columns in the save payload.
 */

/**
 * @typedef {Object} EditState
 * @property {ModalView} view - The modal view.
 * @property {ModalViewContext} context - Information about where the user has opened the modal from.
 * @property {string | null} snapshot - JSON fingerprint of the edit state at load time, used for dirty detection.
 * @property {string | null} draftSheetId - The sheet metadata id of the sheet being edited.
 * @property {EditingSheet | null} draftSheet - A temporary copy of the sheet being edited. Applied on confirm, discarded on cancel.
 * @property {Record<string, EditingSheet>} sheetMap - The sheets being edited (source of truth during editing).
 * @property {string[]} sheetOrder - The order of sheet ids for display.
 * @property {SaveContext} saveContext -
 * @property {string | null} sourceTemplateName
 * @property {number | null} sourceTemplateId
 * @property {number[] | null} sourceSharedWithIds
 * @property {boolean} sourceTemplateIsOwner
 *
 */

/**
 * @typedef {Object} ExportTemplateState
 * @property {SheetMetadata[]} sheetMetadata - The sheet metadata.
 * @property {number | 'all'} selectedTemplateId - The id of the selected export template for export, 'all' means "All fields".
 * @property {boolean} templatesLoaded - Whether the minimal template list has loaded at least once. Gates favourite auto-selection to the first load so the user's selection stays sticky afterwards.
 * @property {number | null} favouriteTemplateId - The id of the user's current favourite template.
 * @property {boolean} isFavouriteLoading - Whether a favourite request is in progress.
 * @property {EditState} edit - The state holding the currently edited template.
 * @property {SearchConfig} searchConfig - The search config.
 * @property {DropdownTemplate[]} dropdownTemplates - Minimal information about a user's available templates.
 * @property {ExportTemplateSheet[] | null} oneTimeExport - One-time export complete payload.
 */

/** @type {EditState} */
export const initialEditState = {
  snapshot: null,
  context: 'template_management',
  saveContext: 'update_existing_template',
  sourceTemplateId: null,
  sourceTemplateName: null,
  sourceSharedWithIds: [],
  sourceTemplateIsOwner: true,
  view: 'closed',
  draftSheetId: null,
  draftSheet: null,
  sheetMap: {},
  sheetOrder: [],
};

/** @type {ExportTemplateState} */
export const initialState = {
  sheetMetadata: [],
  selectedTemplateId: 'all',
  templatesLoaded: false,
  favouriteTemplateId: null,
  isFavouriteLoading: false,
  edit: initialEditState,
  searchConfig: {
    searchFilter: null,
    searchDateOption: null,
    searchDateRange: null,
    pageSize: 10,
    pageNumber: 0,
    orderBy: 'dateCreated',
    order: 'desc',
    mutationCount: 0,
  },
  dropdownTemplates: [],
  oneTimeExport: null,
};
